import { Location } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import { ConfirmationDialogData } from '../../../models/confirmation-dialog-data.dto';
import { InspectionCrudService } from '../../../services/api/inspection-crud/inspection-crud.service';
import { InspectionService } from '../../../services/api/inspection/inspection.service';
import { CubeService, MessageType } from '../../../services/cube/cube.service';
import { LoaderService } from '../../../services/loader/loader.service';
import {
  IdbService,
  WhereConditions,
} from '../../../services/storage/idb.service';
import {
  StorageService,
  STORAGE_KEYS,
} from '../../../services/storage/storage.service';
import { TemplateLoaderService } from '../../../services/template-loader/template-loader.service';
import { UserService } from '../../../services/user/user.service';
import { InspectionStatus } from '../../../shared/configs/inspection-status.config';
import { InspectionType } from '../../../shared/configs/inspection-type.config';
import { StructureType } from '../../../shared/configs/structure-type.config';
import { TunnelConstants } from '../../../shared/configs/tunnel-constants.config';
import { TunnelTemplateVersion } from '../../../shared/configs/tunnel-template-version.config';
import { UserRole } from '../../../shared/configs/user-role.config';

import { FormlyFormOptions } from '@ngx-formly/core';
import { Subscription } from 'dexie';
import { from, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'cube-view-tunnel-inspection',
  templateUrl: './view-tunnel-inspection.component.html',
  styleUrls: ['./view-tunnel-inspection.component.scss'],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill' },
    },
  ],
  standalone: false,
})
export class ViewTunnelInspectionComponent implements OnInit {
  concioCount: number = 0;
  concioNoList: any[] = [];
  defectUpdateStatus: boolean = false;
  devMode = environment.devMode; // TEMP: For development
  fields: any[] = [];
  form: FormGroup = new FormGroup({});
  inspectionStatus: typeof InspectionStatus = InspectionStatus;
  l0BackupData: any = {};
  l1BackupData: any = {};
  l1InitBackupData: any = {};
  l2UpdateStatus: boolean = false;
  levelsList: string[];
  model: any;
  options: FormlyFormOptions;
  token: string;
  tunnelConstants: typeof TunnelConstants = TunnelConstants;
  userRole: typeof UserRole = UserRole;

  private defectUpdateIndex: number;
  private destroy$: Subject<void> = new Subject<void>();
  private inspectionId: string = '';
  private inspectionType: number = 0;
  private isDisabled: boolean = true;
  private isOnline: boolean = true;
  private l1UpdateIndex: number;
  private l1UpdateStatus: boolean = false;
  private l2ConcioNo: number;
  private l2UpdateIndex: number;
  private migrationDelay: number = 2500; // TODO
  private offlineDbModelUpdateId;
  private plannedInspection;
  private saveStatus: boolean = false;
  private templateVersion: TunnelTemplateVersion;
  private tenantId: string;
  private unsubscribe$: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private cubeService: CubeService,
    private idbService: IdbService,
    private inspectionCrudService: InspectionCrudService,
    private inspectionService: InspectionService,
    private loaderService: LoaderService,
    private location: Location,
    private router: Router,
    private storageService: StorageService,
    private templateLoaderService: TemplateLoaderService,
    private userService: UserService
  ) {
    this.inspectionId =
      this.activatedRoute.snapshot.paramMap.get('inspectionId');
    /* // INFO: Functionality not applicable in PORTAL
    this.inspectionType = +this.activatedRoute.snapshot.paramMap.get('type');
    this.templateVersion = this.activatedRoute.snapshot.paramMap.get(
      'templateVersion'
    ) as TunnelTemplateVersion;
    this.tenantId = this.activatedRoute.snapshot.paramMap.get('tenantId'); */
    storageService.addToStorage(STORAGE_KEYS.savedImages, []);
    storageService.addToStorage(STORAGE_KEYS.deletedFiles, []);
    this.levelsList = this.createLevelsList();
    this.model = this.createModel();
    this.options = this.createFormlyOptions();
  }

  ngOnInit(): void {
    this.getInspectionMediaToken().then(() => {
      this.getInspection(this.inspectionId);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addDefectImagesIndexToBeModified(defectList: any[], index: number): void {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  addRepeatSectionImagesIndexToBeModified(
    list: any[],
    index: number,
    key: string
  ): void {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  clearEmptyModel(): void {
    // INFO: Functionality not applicable in PORTAL
  }

  deleteUploadedDefectFiles(urlList: string[]): void {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  deleteUploadedFiles(fileNames: string | string[]): void {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  duplicateDefect(index: number, list: any[], listName: string): void {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  getConfirmation(dialogData?: ConfirmationDialogData): any {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  getDeepCopy(item: any): any {
    // INFO: Used in template
    return this.cubeService.getDeepCopy(item);
  }

  getDefectsList(level: string): any {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  isFieldDisabled(
    rolesList: UserRole[],
    isDisabled: boolean,
    isNotAllowed: boolean = false
  ): boolean {
    // INFO: Used in template
    if (isDisabled) return true;

    const isUserAllowedByRole = rolesList.length
      ? this.userService.isUserAllowedByRole(rolesList)
      : true;

    return isNotAllowed ? isUserAllowedByRole : !isUserAllowedByRole;
  }

  migrateDataToChildLevel(
    index: number,
    destinationLevel: string,
    sourceList
  ): void {
    // INFO: Used in template
    switch (destinationLevel) {
      case TunnelConstants.levels.l1:
        this.concioCount = sourceList[0].modelstructure.conciolist.length;
        /* // INFO: Functionality not applicable in PORTAL
        this.saveDeletedFilesToCurrentLevel(
          this.model[TunnelConstants.levels.l0]
        ); */
        this.l0BackupData = this.getDeepCopy(
          this.model[TunnelConstants.levels.l0]
        );
        this.l1UpdateStatus = true;
        this.l1UpdateIndex = index;

        break;

      case TunnelConstants.levels.l2:
        /* // INFO: Functionality not applicable in PORTAL
        this.saveDeletedFilesToCurrentLevel(
          this.model[TunnelConstants.levels.l1]
        ); */
        this.l1BackupData = this.getDeepCopy(
          this.model[TunnelConstants.levels.l1]
        );
        this.l2UpdateStatus = true;
        this.l2UpdateIndex = index;
        this.l2ConcioNo = sourceList[index][TunnelConstants.partNo];

        break;

      default:
        this.defectUpdateStatus = true;
        this.defectUpdateIndex = index;
        this.inspectionService.setDefectRowIndex$(index);

        /* // Functionality now handled by syncSharedModelDataForList(). The following code can be removed at a later stage.
        // TODO will need migration to update existing inspections
        // Setting Codice IOP for defect levels
        for (const source of sourceList) {
          source.modelstructure['Scheda Ispezione Ponti di Livello 1'][
            'Codice IOP'
          ] = this.model[TunnelConstants.levels.l0]['Codice IOP'];
        } */
        /* // INFO: Functionality not applicable in PORTAL
        this.saveDeletedFilesToCurrentLevel(
          this.model[TunnelConstants.levels.l2]
        ); */

        break;
    }

    this.model[destinationLevel] ??= {};

    this.form = new FormGroup({});
    this.model[destinationLevel] = this.getDeepCopy(
      sourceList[index].modelstructure
    );
    this.options.formState[destinationLevel] = false;
  }

  onClickBack(): void {
    if (
      this.model.status === InspectionStatus.toReview &&
      this.userService.getActiveRole() === UserRole.auditor &&
      [
        TunnelConstants.levels.defect1,
        TunnelConstants.levels.defect2,
        TunnelConstants.levels.defect3,
        TunnelConstants.levels.defect4,
        TunnelConstants.levels.defect5,
      ].includes(this.model.activeLevel)
    ) {
      this.cubeService
        .confirm({
          message: 'templates.unsaved_progress',
        })
        .pipe(
          filter((isConfirmed) => isConfirmed),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.goBack();
        });

      return;
    }

    this.goBack();
  }

  onClickComplete(): void {
    this.reviewInspection(true);
  }

  async onClickSave(): Promise<void> {
    let isValid: boolean;
    switch (this.model.activeLevel) {
      case TunnelConstants.levels.l0: // Livello 0
        this.reviewInspection();

        break;

      case TunnelConstants.levels.l1: // Level 1
        this.migrateDataFromLevel1ToLevel0();

        break;

      case TunnelConstants.levels.l2: // Level 2
        /* // INFO: Functionality not applicable in PORTAL
        isValid = await this.validateConcioNo();
        if (isValid) */
        this.migrateDataFromLevel2ToLevel1();

        break;

      case TunnelConstants.levels.defect1: // Defects Template - 1 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect2: // Defects Template - 2 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect3: // Defects Template - 3 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect4: // Defects Template - Difetti relativi alla piattaforma stradale/autostradale
      case TunnelConstants.levels.defect5: // Defects Template - Difetti relativi agli elementi non strutturali e impianti
        const defectLevel = this.model.activeLevel;
        this.migrateDataFromDefectLevelsToLevel2(
          defectLevel,
          `${defectLevel}list`
        );

        break;
    }
  }

  onClickSubmit(): void {
    this.inspectionService
      .updateInspectionsStatus([this.model.id], InspectionStatus.submitted)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.router.navigate(['/bms-portal/inspections/inspection-report']);
        this.cubeService.showMessage(
          MessageType.Info,
          'templates.inspection_submit_success'
        );
      });
  }

  refreshFields(): void {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  saveModelToIndexedDbForPlannedInspection(): void {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  setImages(level: string): void {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  syncSharedModelData(
    pathsKey: string,
    updatedValue: any,
    changedIndex?: number
  ): void {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  syncSharedModelDataForList(pathsKey: string, updatedValue: any): void {
    // INFO: Used in template
    // INFO: Functionality not applicable in PORTAL
  }

  private createFormlyOptions(): FormlyFormOptions {
    return {
      formState: {
        [TunnelConstants.levels.defect1]: true,
        [TunnelConstants.levels.defect2]: true,
        [TunnelConstants.levels.defect3]: true,
        [TunnelConstants.levels.defect4]: true,
        [TunnelConstants.levels.defect5]: true,
      },
    };
  }

  private createLevelsList(): string[] {
    const levelsList = Object.values(TunnelConstants.levels);

    return [...levelsList];
  }

  private createModel(): any {
    return {};
  }

  private async deleteUnusedImages(): Promise<void> {
    // INFO: Functionality not applicable in PORTAL
  }

  private fetchDefectsList(level: string): void {
    // INFO: Functionality not applicable in PORTAL
  }

  private getInspection(inspectionId: string): void {
    this.inspectionService
      .getInspection(inspectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.model = res.data;
        this.getTemplateByVersion(this.model.version);
      });
  }

  private getInspectionMediaToken(): Promise<void> {
    return this.inspectionService
      .getInspectionMediaToken()
      .pipe(
        map((res) => {
          this.token = res.data.token;
        }),
        takeUntil(this.destroy$)
      )
      .toPromise();
  }

  private async getTemplateByVersion(
    templateVersion: TunnelTemplateVersion
  ): Promise<void> {
    const template = await this.templateLoaderService.loadTunnelTemplate(
      templateVersion,
      {
        component: this,
        isDisabled: this.isDisabled,
        userRole: this.userService.getActiveRole(),
      }
    );
    this.setTemplate(template);
  }

  private goBack(): void {
    switch (this.model.activeLevel) {
      case TunnelConstants.levels.l0: // Livello 0
        this.location.back();

        break;

      case TunnelConstants.levels.l1: // Level 1
        this.l1UpdateStatus = false;
        delete this.l1UpdateIndex;
        /* // INFO: Functionality not applicable in PORTAL
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[TunnelConstants.levels.l1]
        ); */
        this.model[TunnelConstants.levels.l0] = this.l0BackupData;
        this.model[TunnelConstants.levels.l1] = this.l1InitBackupData;
        this.model.activeLevel = TunnelConstants.levels.l0;
        this.refreshFields();

        break;

      case TunnelConstants.levels.l2: // Level 2
        this.l2UpdateStatus = false;
        delete this.l2UpdateIndex;
        delete this.l2ConcioNo;
        /* // INFO: Functionality not applicable in PORTAL
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[TunnelConstants.levels.l2]
        ); */
        this.model[TunnelConstants.levels.l1] = this.l1BackupData;
        this.resetFields();
        this.options.formState[TunnelConstants.levels.l2] = true;
        this.model.activeLevel = TunnelConstants.levels.l1;
        this.refreshFields();

        break;

      case TunnelConstants.levels.defect1: // Defects Template 1 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect2: // Defects Template 2 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect3: // Defects Template 3 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect4: // Defects Template 4 - Difetti relativi alla piattaforma stradale/autostradale
      case TunnelConstants.levels.defect5: // Defects Template 5 - Difetti relativi agli elementi non strutturali e impianti
        this.defectUpdateStatus = false;
        delete this.defectUpdateIndex;
        /* // INFO: Functionality not applicable in PORTAL
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[this.model.activeLevel]
        ); */
        this.options.formState[this.model.activeLevel] = true;
        this.refreshFields();
        this.model.activeLevel = TunnelConstants.levels.l2;

        break;
    }
  }

  private migrateDataFromDefectLevelsToLevel2(
    level: string,
    defectList: string
  ): void {
    const modelValue = this.getDeepCopy(this.model[level]);

    /* // INFO: Functionality not applicable in PORTAL
    if (
      !this.devMode.disableDefectsValidation && // TEMP: Disable defect validations
      !this.validateDefectsTableData(
        modelValue['Scheda Ispezione Ponti di Livello 1'][
          'Scheda Ispezione Ponti di Livello 1 List1'
        ]
      )
    ) {
      this.cubeService.confirm({
        isCancelButtonVisible: false,
        message: 'templates.update_all_defects',
        title: 'confirmation_dialog.title.info',
      });

      return;
    } */

    this.loaderService.requestStarted();
    this.model.activeLevel = TunnelConstants.levels.l2;

    setTimeout(() => {
      /* // INFO: Functionality not applicable in PORTAL
      this.migrateDeletedFilesFromChildToParentLevel(
        TunnelConstants.levels.l2,
        modelValue
      ); */
      if (this.defectUpdateStatus) {
        // Defect edit
        this.model[TunnelConstants.levels.l2][defectList][
          this.defectUpdateIndex
        ] = {
          [TunnelConstants.partNo]:
            this.model[TunnelConstants.levels.l2][TunnelConstants.partNo],
          modelstructure: modelValue,
        };

        this.model[TunnelConstants.levels.l2][defectList] = this.getDeepCopy(
          this.model[TunnelConstants.levels.l2][defectList]
        );
        delete this.defectUpdateIndex;
        this.defectUpdateStatus = false;
      } else {
        // Defect add
        this.model[TunnelConstants.levels.l2][defectList] = this.model[
          TunnelConstants.levels.l2
        ][defectList].concat({
          [TunnelConstants.partNo]:
            this.model[TunnelConstants.levels.l2][TunnelConstants.partNo],
          modelstructure: modelValue,
        });
      }

      this.options.formState[level] = true;
      this.refreshFields();
      this.loaderService.requestEnded();
    }, this.migrationDelay);
  }

  private migrateDataFromLevel1ToLevel0(): void {
    const modelValue = this.getDeepCopy(this.model[TunnelConstants.levels.l1]);

    this.loaderService.requestStarted();
    this.model.activeLevel = TunnelConstants.levels.l0;

    let defect1 = 0;
    let defect2 = 0;
    let defect3 = 0;
    let defect4 = 0;
    let defect5 = 0;

    for (const concio of modelValue.conciolist) {
      defect1 +=
        concio.modelstructure[`${TunnelConstants.levels.defect1}list`].length;
      defect2 +=
        concio.modelstructure[`${TunnelConstants.levels.defect2}list`].length;
      defect3 +=
        concio.modelstructure[`${TunnelConstants.levels.defect3}list`].length;
      defect4 +=
        concio.modelstructure[`${TunnelConstants.levels.defect4}list`].length;
      defect5 +=
        concio.modelstructure[`${TunnelConstants.levels.defect5}list`].length;
    }

    setTimeout(() => {
      /* // INFO: Functionality not applicable in PORTAL
      this.migrateDeletedFilesFromChildToParentLevel(
        TunnelConstants.levels.l0,
        modelValue
      ); */
      if (this.l1UpdateStatus) {
        // Livello 1 edit
        this.model.l0.opereparzialilist[this.l1UpdateIndex] = {
          'N° Concio': modelValue.conciolist.length,
          'N° 1 - Difetti relativi alla struttura': defect1,
          'N° 2 - Difetti relativi alla struttura': defect2,
          'N° 3 - Difetti relativi alla struttura': defect3,
          'N° Difetti relativi alla piattaforma stradale/autostradale': defect4,
          'N° Difetti relativi agli elementi non strutturali e impianti':
            defect5,
          modelstructure: modelValue,
        };
        this.model[TunnelConstants.levels.l0].opereparzialilist =
          this.getDeepCopy(
            this.model[TunnelConstants.levels.l0].opereparzialilist
          );
        this.l1UpdateStatus = false;
        delete this.l1UpdateIndex;
      } else {
        // Livello 1 add
        this.model[TunnelConstants.levels.l0].opereparzialilist = this.model[
          TunnelConstants.levels.l0
        ].opereparzialilist.concat({
          'N° Concio': modelValue.conciolist?.length ?? 0,
          'N° 1 - Difetti relativi alla struttura': defect1,
          'N° 2 - Difetti relativi alla struttura': defect2,
          'N° 3 - Difetti relativi alla struttura': defect3,
          'N° Difetti relativi alla piattaforma stradale/autostradale': defect4,
          'N° Difetti relativi agli elementi non strutturali e impianti':
            defect5,
          modelstructure: modelValue,
        });
      }

      this.options.formState[TunnelConstants.levels.l1] = true;
      this.refreshFields();
      this.loaderService.requestEnded();
    }, this.migrationDelay);
  }

  private migrateDataFromLevel2ToLevel1(): void {
    const modelValue = this.getDeepCopy(this.model[TunnelConstants.levels.l2]);

    this.loaderService.requestStarted();
    this.model.activeLevel = TunnelConstants.levels.l1;

    setTimeout(() => {
      /* // INFO: Functionality not applicable in PORTAL
      this.migrateDeletedFilesFromChildToParentLevel(
        TunnelConstants.levels.l1,
        modelValue
      ); */
      if (this.l2UpdateStatus) {
        // Level 2 edit
        this.model[TunnelConstants.levels.l1].conciolist[this.l2UpdateIndex] = {
          [TunnelConstants.partNo]: modelValue[TunnelConstants.partNo],
          modelstructure: modelValue,
        };
        this.model[TunnelConstants.levels.l1].conciolist = this.getDeepCopy(
          this.model[TunnelConstants.levels.l1].conciolist
        );

        this.l2UpdateStatus = false;
        delete this.l2UpdateIndex;
        delete this.l2ConcioNo;
      } else {
        // Level 2 add
        this.model[TunnelConstants.levels.l1].conciolist = this.model[
          TunnelConstants.levels.l1
        ].conciolist.concat({
          [TunnelConstants.partNo]: modelValue[TunnelConstants.partNo],
          modelstructure: modelValue,
        });
      }

      this.model[TunnelConstants.levels.l2] = {
        [`${TunnelConstants.levels.defect1}list`]: [],
        [`${TunnelConstants.levels.defect2}list`]: [],
        [`${TunnelConstants.levels.defect3}list`]: [],
        [`${TunnelConstants.levels.defect4}list`]: [],
        [`${TunnelConstants.levels.defect5}list`]: [],
      };

      this.options.formState[TunnelConstants.levels.l2] = true;
      this.refreshFields();
      this.loaderService.requestEnded();
    }, this.migrationDelay);
  }

  private resetFields(): void {
    // INFO: Functionality not applicable in PORTAL
  }

  private reviewInspection(isCompleted: boolean = false): void {
    const modelData = this.getDeepCopy(this.model);
    if (isCompleted) {
      modelData.status = InspectionStatus.completed;
    }
    modelData.activeLevel = 'l0';
    const formData = new FormData();
    formData.append('data', JSON.stringify(modelData));
    this.inspectionCrudService
      .reviewInspection(modelData.id, formData)
      .subscribe((res) => {
        this.router.navigate(['/bms-portal/inspections/inspection-report']);
        this.cubeService.showMessage(
          MessageType.Info,
          'templates.inspection_save_success'
        );
      });
  }

  private setTemplate(template: any): void {
    // Getting dynamic model structure from template
    const dynamicModelStructure = template.shift();

    this.fields = template;
  }

  private validateDefectsTableData(tableData: any[]): any {
    // INFO: Functionality not applicable in PORTAL
  }
}
